import { useCallback, useEffect, useRef, useState } from 'react';
import { BoxProp } from '../types/Shipment.types';
import { DataService } from '../services/data-service';
import { useAppDispatch } from '../redux/hooks';
import { showAlert } from '../redux/alert/alertReducer';

interface UseAutoSaveBoxItemsProps {
  box: BoxProp | null;
  enabled?: boolean;
  debounceMs?: number; // Delay after last change before saving
  maxInterval?: number; // Maximum time between saves
  retryAttempts?: number; // Number of retry attempts for failed saves
}

export const useAutoSaveBoxItems = ({
  box,
  enabled = true,
  debounceMs = 5000, // 5 seconds after last change
  maxInterval = 120000, // 2 minutes maximum interval
  retryAttempts = 3
}: UseAutoSaveBoxItemsProps) => {
  const dispatch = useAppDispatch();
  const [isAutoSaving, setIsAutoSaving] = useState(false);

  // Optimized hash for large datasets - only track essential data
  const lastSavedHash = useRef<string>('');
  const lastSaveTime = useRef(0);
  const pendingChanges = useRef(false);
  const saveTimeout = useRef<NodeJS.Timeout>();
  const maxIntervalTimer = useRef<NodeJS.Timeout>();
  const retryCount = useRef(0);

  // Create lightweight hash for comparison (much faster than JSON.stringify)
  const createItemsHash = useCallback((items: any[]) => {
    if (!items?.length) return '';
    return items
      .map(item => `${item.id}:${item.barcode}:${item.qty}`)
      .sort() // Ensure consistent ordering
      .join('|');
  }, []);

  // Optimized save function with retry logic
  const saveBoxItems = useCallback(async (forceSave = false, showSuccessMessage = false) => {
    if (!box || !enabled) return false;

    // Create hash for current items
    const currentHash = createItemsHash(box.boxItems);

    // Skip if no changes and not forced
    if (!forceSave && lastSavedHash.current === currentHash) {
      return true;
    }

    // Prevent concurrent saves
    if (isAutoSaving) return false;

    setIsAutoSaving(true);
    pendingChanges.current = false;

    try {
      const payload = {
        id: box.id,
        boxStatus: box.status,
        boxItems: box.boxItems.map((item) => ({
          Id: item.id,
          Barcode: item.barcode,
          Qty: item.qty
        }))
      };

      const response = await DataService.post('api/Box/update-items', payload);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Success - update tracking variables
      lastSavedHash.current = currentHash;
      lastSaveTime.current = Date.now();
      retryCount.current = 0;

      // Show success message only for manual saves or if explicitly requested
      if (showSuccessMessage) {
        dispatch(
          showAlert({
            message: `Box ${box.refNumber} items saved successfully (${box.boxItems.length} items)`,
            type: 'success'
          })
        );
      }

      return true;
    } catch (err: any) {
      retryCount.current++;

      console.warn(`Auto-save failed (attempt ${retryCount.current}/${retryAttempts}):`, err.message);

      // Retry logic for network errors
      if (retryCount.current < retryAttempts) {
        setTimeout(() => {
          saveBoxItems(forceSave, false); // Retry without showing success message
        }, 2000 * retryCount.current); // Exponential backoff
      } else {
        // Show error only after all retries failed
        dispatch(
          showAlert({
            message: `Failed to save box items after ${retryAttempts} attempts. Your data is backed up locally.`,
            type: 'warning'
          })
        );
        retryCount.current = 0; // Reset for next attempt
      }

      return false;
    } finally {
      setIsAutoSaving(false);
    }
  }, [box, enabled, isAutoSaving, createItemsHash, retryAttempts, dispatch]);

  // Debounced save - saves after period of inactivity
  const scheduleDebouncedSave = useCallback(() => {
    // Clear existing timeout
    if (saveTimeout.current) {
      clearTimeout(saveTimeout.current);
    }

    // Mark that we have pending changes
    pendingChanges.current = true;

    // Schedule save after debounce period
    saveTimeout.current = setTimeout(() => {
      if (pendingChanges.current) {
        saveBoxItems(false, false);
      }
    }, debounceMs);
  }, [debounceMs, saveBoxItems]);

  // Maximum interval timer - ensures saves happen even without changes
  const scheduleMaxIntervalSave = useCallback(() => {
    if (maxIntervalTimer.current) {
      clearTimeout(maxIntervalTimer.current);
    }

    maxIntervalTimer.current = setTimeout(() => {
      const timeSinceLastSave = Date.now() - lastSaveTime.current;

      // Only save if we have pending changes and enough time has passed
      if (pendingChanges.current && timeSinceLastSave >= maxInterval) {
        saveBoxItems(false, false);
      }

      // Schedule next max interval check
      scheduleMaxIntervalSave();
    }, maxInterval);
  }, [maxInterval, saveBoxItems]);

  // Monitor box items changes
  useEffect(() => {
    if (!enabled || !box?.boxItems) return;

    const currentHash = createItemsHash(box.boxItems);

    // Only schedule save if items actually changed
    if (currentHash !== lastSavedHash.current) {
      scheduleDebouncedSave();
    }

    // Cleanup function
    return () => {
      if (saveTimeout.current) {
        clearTimeout(saveTimeout.current);
      }
    };
  }, [box?.boxItems, enabled, createItemsHash, scheduleDebouncedSave]);

  // Start max interval timer when box opens
  useEffect(() => {
    if (!enabled || !box?.id) return;

    scheduleMaxIntervalSave();

    return () => {
      if (maxIntervalTimer.current) {
        clearTimeout(maxIntervalTimer.current);
      }
    };
  }, [box?.id, enabled, scheduleMaxIntervalSave]);

  // Critical save points
  useEffect(() => {
    if (!enabled || !box?.id) return;

    // Save before page unload with sendBeacon for reliability
    const handleBeforeUnload = () => {
      if (pendingChanges.current && box.boxItems?.length > 0) {
        const payload = JSON.stringify({
          id: box.id,
          boxStatus: box.status,
          boxItems: box.boxItems.map((item) => ({
            Id: item.id,
            Barcode: item.barcode,
            Qty: item.qty
          }))
        });

        // Use sendBeacon for reliable delivery
        navigator.sendBeacon('/api/Box/update-items', payload);
      }
    };

    // Save when tab becomes hidden (user switches tabs)
    const handleVisibilityChange = () => {
      if (document.hidden && pendingChanges.current) {
        saveBoxItems(true, false); // Force save without message
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [box?.id, box?.boxItems, box?.status, enabled, saveBoxItems]);

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      if (saveTimeout.current) clearTimeout(saveTimeout.current);
      if (maxIntervalTimer.current) clearTimeout(maxIntervalTimer.current);
    };
  }, []);

  return {
    saveBoxItems: (showMessage = true) => saveBoxItems(true, showMessage),
    isAutoSaving,
    hasPendingChanges: pendingChanges.current
  };
};